import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Chip } from '@/components/forge/Chip'
import { Field, TextArea } from '@/components/forge/Field'
import { ForgeButton } from '@/components/forge/ForgeButton'
import { ForgeDialog } from '@/components/forge/ForgeDialog'
import { ForgeSelect, StaticField } from '@/components/forge/ForgeSelect'
import { Eyebrow, Mono } from '@/components/forge/primitives'
import { PlaceField } from '@/components/forge/PlaceField'
import { useHierarchyNode } from '@/features/hierarchy/queries'
import { ATTACHABLE_TIERS, placeOf, trioOf } from '@/features/hierarchy/place'
import { NOTES_MAX_LENGTH, WO_PRIORITIES } from '@/features/workOrders/api'

/**
 * Raise a work order, or edit one.
 *
 * LIVE. The design's fields — title, asset, priority, assignee, due, status,
 * source — minus the last two, plus the two the API has and the design does
 * not: notes, and the anomaly this order answers.
 *
 * **Status is not here.** The workflow owns it. A form that can set a new order
 * to Completed makes the six-column board decorative.
 *
 * **Source is not here either.** It is provenance: the AI badge means Forge AI
 * drafted the order, and a select would let anybody award themselves one. The
 * server stamps `human` on anything raised through this form and the dialog says
 * so, rather than silently dropping a field the design showed.
 *
 * **The asset is a select over the plant.** The prototype takes it as free text,
 * so "Comressor C-12" is a work order against nothing — and the board, the asset
 * page and the line rollups all quietly lose it.
 *
 * **The anomaly is a field, not only a URL parameter.** `WorkOrderCreate` takes
 * an `anomaly_id`, and until now the only way to set it was to arrive at
 * `?new=1&alertId=…` from the anomaly's own screen. Anyone opening New WO from
 * the board could not link the order to the thing that caused it — the record
 * that explains why the job exists — and the form even held the id in state
 * without rendering a control for it. It is Create-only: `WorkOrderUpdate` has
 * no such field, so on edit the link is stated as a fact rather than offered.
 *
 * **The due date is a date, and goes out as one.** `due_date` is a `date`
 * column, so the input's own `yyyy-mm-dd` IS the wire value and nothing
 * converts it — an offset in days was the old shape and every round trip
 * through it risked a day. The prototype's is a text box with the placeholder
 * "Jul 24", which stores a string that cannot be sorted, compared or gone
 * overdue.
 */
export function WorkOrderFormDialog({
  open,
  order,
  prefill,
  assignees,
  anomalies,
  canPickAnomaly,
  anomaliesFailed,
  pending,
  onClose,
  onSubmit,
}) {
  const { t } = useTranslation(['app', 'common'])
  const editing = Boolean(order)

  /* The draft initialises once per opening. The PARENT gives this component a
     `key` that changes with the order it was opened on, so React remounts it
     and this runs again — rather than an effect that sets state during render,
     which is a cascading render and is what the lint rule is about. */
  const [form, setForm] = useState(() => initial(order, prefill))

  const set = (key, value) => setForm((previous) => ({ ...previous, [key]: value }))

  /* A prefill from another module arrives as `?assetId=` alone, with no name.
     The one machine is read to name it, rather than the whole estate. */
  const unnamed = form.place && !form.place.name ? form.place : null
  const prefilledNode = useHierarchyNode(unnamed ? { tier: unnamed.tier, id: unnamed.id } : null)
  const place = unnamed
    ? { ...unnamed, name: prefilledNode.data?.name ?? unnamed.id }
    : form.place

  /**
   * The anomaly chosen, resolved from the list rather than held beside its id.
   *
   * Null when nothing is linked, and also when a linked id is not in the list —
   * a prefill for a resolved anomaly, or one this reader cannot see. Both are
   * real states and neither may crash the form.
   */
  const linkedAnomaly = anomalies.find((entry) => entry.id === form.anomalyId) ?? null

  /**
   * ── The anomaly DECIDES where the order attaches ───────────────────────
   * `verify_anomaly_matches_parent` compares the order's
   * `(plant_id, line_id, machine_id)` against the anomaly's own, exactly, and
   * 400s on any difference. So these cannot be two independent controls: an
   * anomaly attached at a LINE can never match a machine-level order, and
   * filtering the anomaly list by the selected machine would only hide the
   * plant- and line-level ones rather than make them selectable.
   *
   * So the anomaly wins. Pick one and the placement follows it and is stated;
   * pick none and the asset select works as it did. This is also the reason the
   * list is not filtered by asset — every open anomaly is selectable, and
   * choosing it moves the order to where the anomaly already is.
   */
  const parent = linkedAnomaly
    ? { plantId: linkedAnomaly.plantId, lineId: linkedAnomaly.lineId, machineId: linkedAnomaly.machineId }
    : (trioOf(place) ?? { plantId: null, lineId: null, machineId: null })

  const hasParent = Boolean(parent.plantId || parent.lineId || parent.machineId)
  const valid = form.title.trim().length > 2 && hasParent

  return (
    <ForgeDialog
      open={open}
      onOpenChange={(next) => !next && onClose()}
      size="md"
      title={
        editing ? t('workOrders.editTitle', { id: order.serialNumber }) : t('workOrders.newTitle')
      }
      footer={
        <div className="flex flex-1 flex-wrap justify-end gap-2">
          <ForgeButton onClick={onClose}>{t('common:actions.cancel')}</ForgeButton>
          <ForgeButton
            variant="primary"
            disabled={!valid || pending}
            onClick={() =>
              onSubmit({
                title: form.title,
                notes: form.notes,
                anomalyId: form.anomalyId,
                priority: form.priority,
                // A list, because `assignee_ids` is. One picker fills it today.
                assigneeIds: form.assigneeId ? [form.assigneeId] : [],
                dueDate: form.due,
                /* Create only. Exactly one of the three is set — from the linked
                   anomaly where there is one, and from the asset picker where
                   there is not. `ExactlyOneParentMixin` refuses zero or two. */
                ...parent,
              })
            }
          >
            {editing ? t('workOrders.save') : t('workOrders.create')}
          </ForgeButton>
        </div>
      }
    >
      <div className="flex flex-col gap-5">
        <Field
          label={t('workOrders.form.title')}
          required
          value={form.title}
          onChange={(event) => set('title', event.target.value)}
          placeholder={t('workOrders.form.titlePlaceholder')}
        />

        {/* ── Where it attaches ────────────────────────────────────────────
            A `StaticField` and not a disabled select whenever the answer is
            already decided — by the anomaly, or by the order having been
            created. A greyed-out control looks like a control, takes a hover,
            and teaches somebody to keep trying it. */}
        {linkedAnomaly ? (
          <StaticField
            label={t('workOrders.form.place')}
            hint={t('workOrders.form.assetFromAnomaly')}
            icon="machine"
          >
            <span className="min-w-0 truncate">
              {[
                linkedAnomaly.hierarchy.machine,
                linkedAnomaly.hierarchy.line,
                linkedAnomaly.hierarchy.plant,
              ]
                .filter(Boolean)
                .join(' · ')}
            </span>
          </StaticField>
        ) : (
          /* The shared "choose a place" dialog — see `PlaceField`. Any one of
             site, line or machine: `WorkOrderCreate` takes `ExactlyOneParentMixin`,
             and the flat select it replaced offered machines only because a list
             of the whole estate could not hold more. */
          <PlaceField
            label={t('workOrders.form.place')}
            required
            value={place}
            onChange={(next) => set('place', next)}
            allowTiers={ATTACHABLE_TIERS}
            placeholder={t('workOrders.form.placePlaceholder')}
            // Locked on edit: moving a work order to another place is not an
            // edit, it is a different order. The same call `updateSensor` makes
            // about a sensor's parent.
            disabled={editing}
            hint={editing ? t('workOrders.form.placeLocked') : undefined}
          />
        )}

        <div>
          <Eyebrow className="mb-2">{t('workOrders.form.priority')}</Eyebrow>
          <div
            role="group"
            aria-label={t('workOrders.form.priority')}
            className="flex flex-wrap gap-1.5"
          >
            {WO_PRIORITIES.map((priority) => (
              <Chip
                key={priority}
                selected={form.priority === priority}
                onClick={() => set('priority', priority)}
              >
                {t(`workOrders.priority.${priority}`)}
              </Chip>
            ))}
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <ForgeSelect
            label={t('workOrders.form.assignee')}
            value={form.assigneeId || 'none'}
            onValueChange={(value) => set('assigneeId', value === 'none' ? '' : value)}
            options={[
              { value: 'none', label: t('workOrders.unassigned') },
              ...assignees.map((person) => ({ value: person.id, label: person.name })),
            ]}
          />
          <Field
            label={t('workOrders.form.due')}
            type="date"
            value={form.due}
            onChange={(event) => set('due', event.target.value)}
          />
        </div>

        {/* ── The field the form did not have ──────────────────────────────
            `notes` is a real column — `str | None`, up to 2000 — and until now
            nothing wrote to it. A work order whose only content is its title
            gives a technician the thing to touch and nothing about what to do
            to it, which is most of why a paper job card exists.

            It replaced the estimate box, which was the opposite case: a control
            collecting a number the API has no column for, so every hour anybody
            typed was discarded on save. */}
        <TextArea
          rows={4}
          label={t('workOrders.form.notes')}
          maxLength={NOTES_MAX_LENGTH}
          value={form.notes}
          onChange={(event) => set('notes', event.target.value)}
          placeholder={t('workOrders.form.notesPlaceholder')}
          hint={t('workOrders.form.notesHint')}
        />

        {/* Where the two dropped fields went, said out loud rather than left for
            somebody to notice. */}
        <p className="m-0 rounded-sm bg-sunken px-3.5 py-2.5 text-body-sm text-ink-dim">
          {editing
            ? t('workOrders.form.editNote', {
                state: t(`workOrders.state.${order.status}`),
              })
            : t('workOrders.form.createNote')}
        </p>

        {/* ── The anomaly this order answers ───────────────────────────────
            Create-only, because the column is: `WorkOrderCreate` carries
            `anomaly_id` and `WorkOrderUpdate` does not, so offering it on an
            edit would be a control whose value Pydantic drops without
            complaining. On edit the link is stated instead.

            Arriving from an anomaly's own screen pre-selects it and the select
            still shows it, so the reader can see what was chosen for them and
            change it — a prefill that cannot be inspected is a decision made
            off-screen. */}
        {editing ? (
          form.anomalyId ? (
            <p className="m-0 flex flex-wrap items-center gap-2 text-body-sm text-ink-dim">
              {t('workOrders.form.linkedAnomaly')} <Mono>{form.anomalyId}</Mono>
            </p>
          ) : null
        ) : canPickAnomaly && anomaliesFailed ? (
          /* The register could not be read — it is live-only, so this is also
             what mock mode shows. Said rather than drawn as an empty select,
             which reads as "there are no open anomalies" and is a different
             claim entirely. A prefilled link still travels with the create. */
          <p className="m-0 text-body-sm text-on-amber">
            {form.anomalyId
              ? t('workOrders.form.anomalyListFailedLinked', { id: form.anomalyId })
              : t('workOrders.form.anomalyListFailed')}
          </p>
        ) : canPickAnomaly ? (
          <ForgeSelect
            label={t('workOrders.form.anomaly')}
            hint={t('workOrders.form.anomalyHint')}
            value={form.anomalyId || NO_ANOMALY}
            onValueChange={(value) => set('anomalyId', value === NO_ANOMALY ? null : value)}
            options={[
              { value: NO_ANOMALY, label: t('workOrders.form.noAnomaly') },
              ...anomalies.map((anomaly) => ({
                value: anomaly.id,
                label: `${anomaly.serialNumber} · ${anomaly.title}`,
              })),
              /* A prefilled id the list does not carry — it is resolved,
                 dismissed, or on another machine than the one selected. Kept as
                 an option rather than dropped, because silently clearing
                 somebody's link is worse than showing it unlabelled. */
              ...(form.anomalyId && !anomalies.some((a) => a.id === form.anomalyId)
                ? [{ value: form.anomalyId, label: form.anomalyId }]
                : []),
            ]}
          />
        ) : form.anomalyId ? (
          /* No `anomalies.manage`, so the list cannot be read. The link still
             travels with the create — it just cannot be browsed. */
          <p className="m-0 flex flex-wrap items-center gap-2 text-body-sm text-ink-dim">
            {t('workOrders.form.linkedAnomaly')} <Mono>{form.anomalyId}</Mono>
          </p>
        ) : null}
      </div>
    </ForgeDialog>
  )
}

/** "Not linked to an anomaly". Radix rejects an item whose value is empty. */
const NO_ANOMALY = '__no_anomaly__'

/** Today plus n days, as the `yyyy-mm-dd` a date input wants. */
function isoDate(offsetDays = 0) {
  const date = new Date()
  date.setDate(date.getDate() + offsetDays)
  return date.toISOString().slice(0, 10)
}


function initial(order, prefill) {
  if (order) {
    return {
      title: order.titleText ?? '',
      notes: order.notes ?? '',
      // Wherever the order hangs, with the names the response carries. Locked
      // on edit either way.
      place: placeOf(order),
      priority: order.priority ?? 'medium',
      assigneeId: order.assigneeIds[0] ?? '',
      // Straight from the stored date rather than reconstructed from an offset:
      // `due_date` is already the `yyyy-mm-dd` a date input wants.
      due: order.dueDate ?? isoDate(7),
      anomalyId: order.anomalyId ?? null,
    }
  }
  return {
    title: prefill?.title ?? '',
    notes: '',
    // `?assetId=` is a machine id and nothing else; its name is read on open.
    place: prefill?.assetId ? { id: prefill.assetId, tier: 'asset', name: null } : null,
    priority: prefill?.priority ?? 'medium',
    assigneeId: '',
    due: isoDate(7),
    anomalyId: prefill?.anomalyId ?? null,
  }
}
